import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AccessRole, ButtonColumnType, CustomTableComponent, DataColumnType, ICustomTable, IDropdownSelect, IHeader, LoadingPanelService, LocalService } from 'lib-base';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DropdownModule } from 'primeng/dropdown';
import { TableModule } from 'primeng/table';
import { SecDependenciaUsuarioService } from '../../../../../services/database/secDependenciaUsuario.service';
import { AddDependenciaUsuarioRequest, IGetUsuariosByDependencia } from '../../../../../shared/interfaces/database/seguridad';
import { ButtonModule } from 'primeng/button';
import { IGetUsuariosValidos } from '../../../../../shared/interfaces/database/tramite';
import { firstValueFrom, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-admin-dependencia-usuarios',
  standalone: true,
  imports: [
    CommonModule, FormsModule, CustomTableComponent,
    TableModule, ConfirmPopupModule, DropdownModule, ButtonModule
  ],
  providers: [ConfirmationService],
  templateUrl: './admin-dependencia-usuarios.component.html',
  styleUrls: ['./admin-dependencia-usuarios.component.scss']
})
export class AdminDependenciaUsuariosComponent implements OnInit {
  @Input() dependenciaId!: number;

  trans: any = {};
  customTable !: ICustomTable;
  @ViewChild(CustomTableComponent) customTableComponent !: CustomTableComponent;
  role = AccessRole;

  constructor(
    private readonly _sdu: SecDependenciaUsuarioService, public readonly ls: LocalService, private readonly _t: TranslateService,
    private readonly _ms: MessageService, private readonly _cs: ConfirmationService, private readonly _lps: LoadingPanelService
  ) {
  }

  ngOnInit(): void {
    this.translate();
    this.getUsuariosByDependencia();
    this.getUsuariosDisponiblesParaAgregarADependencia();
  }

  private async translate(): Promise<void> {
    this.trans = await firstValueFrom(forkJoin({
      usuarios: this._t.get('common.usuarios'),
      correo: this._t.get('common.correo'),
      nombre: this._t.get('common.nombre'),
      apellido: this._t.get('common.apellido'),
      rol: this._t.get('common.rol'),
    }));

    this.customTable = {
      header: {
        title: this.trans.usuarios,
        class: 'title font-roboto-condensed font-18 title-blue text-uppercase font-normal mb-0',
      } as IHeader,
      columns: [
        {
          field: 'correo',
          header: this.trans.correo,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: true,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'nombre',
          header: this.trans.nombre,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: true,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'apellido',
          header: this.trans.apellido,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: true,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'cargocodificacion',
          header: this.trans.rol,
          sort: true,
          class: 'font-13',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: ''
          },
          columnFilter: true,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'delete',
          header: '',
          width: '0rem',
          class: 'pt-2 pb-2 pl-0',
          buttonType: ButtonColumnType.DELETE,
          onClick: (event: Event, data: IGetUsuariosByDependencia) => this.deleteConfirmation(event, data),
          disabled: !this.ls.hasRole(this.role.ADMIN_SEC)
        }
      ],
      settings: {
        table: {
          rows: 5,
        }
      }
    } as ICustomTable;
  }

  deleteConfirmation(event: Event, data: IGetUsuariosByDependencia) {
    this._cs.confirm({
      target: event.target as EventTarget,
      message: 'Estas seguro de que quieres eliminar el usuario de la dependencia?',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deleteDependenciaUsuario(data.id);
      },
      reject: () => {

      }
    });
  }

  //#region API Calls
  getUsuariosByDependencia() {
    this._sdu.getUsuariosByDependencia(this.dependenciaId).subscribe({
      next: (data: IGetUsuariosByDependencia[]) => {
        this.customTableComponent.data = data;
      }
    });
  }

  getUsuariosDisponiblesParaAgregarADependencia() {
    this._sdu.getUsuariosDisponiblesParaAgregarADependencia(this.dependenciaId).subscribe({
      next: (usuarios: IGetUsuariosValidos[]) => {
        let dropdown = {
          data: usuarios,
          label: 'correo',
          disabled: !this.ls.hasRole(this.role.ADMIN_SEC),
          onBtnClick: (event: Event, data: any) => this.addUsuarioADependencia(data),
        } as IDropdownSelect

        this.customTableComponent.setHeaderDropdownSelect(dropdown);
      }
    });
  }

  deleteDependenciaUsuario(usuarioId: number) {
    this._lps.show(true);
    this._sdu.deleteDependenciaUsuario(this.dependenciaId, usuarioId).subscribe({
      next: (response: string) => {
        this._lps.hide();
        this.getUsuariosByDependencia();
        this.getUsuariosDisponiblesParaAgregarADependencia();
        this._ms.add({ severity: 'success', summary: 'Usuario Removido', detail: response });
      }
    });
  }

  addUsuarioADependencia(data: IGetUsuariosValidos) {
    let newDependenciaUsuario = {
      dependenciaId: this.dependenciaId,
      usuarioId: data.id
    } as AddDependenciaUsuarioRequest;
    this._lps.show(true);
    this._sdu.addDependenciaUsuario(newDependenciaUsuario).subscribe({
      next: (response: string) => {
        this._lps.hide();
        this.getUsuariosByDependencia();
        this.getUsuariosDisponiblesParaAgregarADependencia();
        this._ms.add({ severity: 'success', summary: 'Éxito', detail: response });
      }
    });
  }
  //#endregion
}
